"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Mail, Phone, MapPin, MessageSquare, Upload } from "lucide-react";
import { Link } from "@/components/common/Link";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light/50 border border-primary/20 rounded-lg text-center">
        <div className="mb-4 text-primary">
          <Mail className="size-12 mx-auto" />
        </div>
        <p className="text-foreground text-lg font-semibold mb-2">
          {successMessage}
        </p>
        <p className="text-muted-foreground mb-6">
          Nos pondremos en contacto contigo pronto.
        </p>
        <Button onClick={resetForm} variant="outline" className="btn-primary">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        },
        maxLength: {
          value: 50,
          message: "Máximo 50 caracteres"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up">
              <FormLabel>Nombre completo</FormLabel>
              <FormControl>
                <Input placeholder="Tu nombre" {...field} className="focus:border-primary/60 focus:ring-primary/30 transition-all duration-[var(--duration-medium)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo electrónico es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo electrónico no válido"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-100">
              <FormLabel>Correo electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="tu@email.com" {...field} className="focus:border-primary/60 focus:ring-primary/30 transition-all duration-[var(--duration-medium)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "El teléfono es obligatorio",
        pattern: {
          value: /^[0-9+\-\s()]+$/,
          message: "Número de teléfono no válido"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-200">
              <FormLabel>Teléfono</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+57 300 123 4567" {...field} className="focus:border-primary/60 focus:ring-primary/30 transition-all duration-[var(--duration-medium)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "El mensaje es obligatorio",
        minLength: {
          value: 10,
          message: "Mínimo 10 caracteres"
        }
      }} render={({
        field
      }) => <FormItem className="animate-fade-in-up-delay-200">
              <FormLabel>Mensaje</FormLabel>
              <FormControl>
                <Textarea placeholder="Cuéntanos brevemente sobre tu fachada o edificio.&#10;Opcional: adjunta fotos para un diagnóstico rápido." className="min-h-32 focus:border-primary/60 focus:ring-primary/30 transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="photos" rules={{
        validate: value => {
          if (!value || value.length === 0) return true;
          if (value.length > 5) {
            return "Máximo 5 fotos permitidas";
          }
          return true;
        }
      }} render={({
        field: {
          value,
          onChange,
          ...field
        }
      }) => <FormItem className="animate-fade-in-up-delay-200">
              <FormLabel>Fotos (opcional)</FormLabel>
              <FormControl>
                <Input type="file" accept="image/*" multiple {...field} onChange={e => {
            const files = e.target.files;
            onChange(files ? Array.from(files) : []);
          }} className="focus:border-primary/60 focus:ring-primary/30 transition-all duration-[var(--duration-medium)] cursor-pointer file:cursor-pointer file:rounded-md file:px-3 file:py-1.5 file:mr-3 file:bg-primary/10 file:text-primary file:hover:bg-primary/20 file:transition-colors" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">
                <Upload className="size-3 inline mr-1" />
                Adjunta hasta 5 fotos en máxima resolución
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary h-12 text-base font-semibold shadow-medium hover:shadow-strong">
          {isSubmitting ? <>
              <span className="animate-spin mr-2">⏳</span>
              Enviando...
            </> : <>
              <Mail className="mr-2" />
              Solicitar diagnóstico gratuito
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section className="py-16 px-4 bg-light-background">
      <div className="max-w-[600px] mx-auto">
        <div ref={ref} className={`bg-white rounded-lg shadow-soft p-8 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        boxShadow: "var(--shadow-soft)"
      }}>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-display font-bold text-foreground mb-3">
              Contáctanos
            </h2>
            <p className="text-muted-foreground text-base">
              Completa el formulario y recibe un diagnóstico gratuito para tu
              proyecto
            </p>
          </div>

          <FormProvider className="w-full" formId="contact-form-hero" sectionName="contact-section" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: "",
          photos: []
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          },
          message: {
            required: true,
            minLength: 10
          },
          photos: {
            custom: value => {
              if (!value || value.length === 0) return null;
              if (value.length > 5) return "Máximo 5 fotos permitidas";
              return null;
            }
          }
        }} submitText="Solicitar diagnóstico gratuito" successMessage="¡Gracias por contactarnos! Nos pondremos en contacto contigo pronto.">
            <ContactFormContent />
          </FormProvider>

          <div className="mt-8 pt-6 border-t border-border/50">
            <div className="flex flex-col sm:flex-row gap-4 justify-center text-sm text-muted-foreground">
              <div className="flex items-center justify-center gap-2">
                <MapPin className="size-4 text-primary" />
                <span>Bogotá, Medellín, Cartagena</span>
              </div>
              <Link to="https://wa.me/573162738270" newTab className="flex items-center justify-center gap-2 text-[hsl(142,70%,49%)] hover:text-[hsl(142,70%,39%)] transition-colors duration-200">
                <MessageSquare className="size-4" />
                <span>WhatsApp</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}